import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore, useHouseholdStore } from './store/useStore.js';

// Auth Components
import LoginForm from './components/auth/LoginForm.jsx';
import MagicLinkSent from './components/auth/MagicLinkSent.jsx';
import ProtectedRoute from './components/auth/ProtectedRoute.jsx';

// Profile Components
import OnboardingFlow from './components/profile/OnboardingFlow.jsx';

// House Components
import HouseAnalyzer from './components/houses/HouseAnalyzer.jsx';
import HouseDetails from './components/houses/HouseDetails.jsx';
import PublicHouseView from './components/share/PublicHouseView.jsx';
import Dashboard from './pages/Dashboard.jsx';

// Legacy App Component (for now)
import OldApp from './App.jsx';

function AuthFlow() {
  const [authStep, setAuthStep] = useState('login'); // 'login', 'magic-sent'
  const [email, setEmail] = useState('');
  const [devToken, setDevToken] = useState('');
  const [linkError, setLinkError] = useState('');
  const [processedToken, setProcessedToken] = useState(null);

  const { init, verifyMagicLink } = useAuthStore();

  const handleMagicLinkSent = (sentEmail, token) => {
    setEmail(sentEmail);
    setDevToken(token);
    setLinkError('');
    setAuthStep('magic-sent');
  };

  const handleVerified = () => {
    // Re-run auth initialization so the main app mounts without a full reload
    init();
  };

  useEffect(() => {
    const url = new URL(window.location.href);
    const magicToken = url.searchParams.get('token');

    if (!magicToken || processedToken === magicToken) {
      return;
    }

    setProcessedToken(magicToken);
    setAuthStep('magic-sent');
    setEmail((prev) => prev || 'your email');
    setDevToken('');
    setLinkError('');

    const verify = async () => {
      try {
        await verifyMagicLink(magicToken);

        url.searchParams.delete('token');
        window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
        handleVerified();
      } catch (error) {
        const message = error?.message || 'Magic link is invalid or has expired. Please request a new link.';
        setLinkError(message);
        url.searchParams.delete('token');
        window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
        setAuthStep('login');
      }
    };

    verify();
  }, [processedToken, verifyMagicLink]);

  return (
    <>
      {authStep === 'login' && (
        <LoginForm onMagicLinkSent={handleMagicLinkSent} externalError={linkError} />
      )}
      {authStep === 'magic-sent' && (
        <MagicLinkSent 
          email={email} 
          devToken={devToken}
          onVerified={handleVerified}
        />
      )}
    </>
  );
}

function MainApp() {
  const { isAuthenticated, init } = useAuthStore();
  const { household, loadHousehold } = useHouseholdStore();
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeApp = async () => {
      // Initialize auth
      await init();
      
      if (isAuthenticated) {
        try {
          // Load household profile
          const householdData = await loadHousehold();
          
          // Check if onboarding is needed
          if (!householdData || !householdData.annual_income) {
            setNeedsOnboarding(true);
          }
        } catch (error) {
          // If household doesn't exist, user needs onboarding
          setNeedsOnboarding(true);
        }
      }
      
      setLoading(false);
    };

    initializeApp();
  }, [isAuthenticated, init, loadHousehold]);

  const handleOnboardingComplete = () => {
    setNeedsOnboarding(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading AvRi...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/share/:token" element={<PublicHouseView />} />
        
        {/* Protected Routes */}
        <Route path="/*" element={
          <ProtectedRoute onUnauthenticated={() => setLoading(false)}>
            {needsOnboarding ? (
              <OnboardingFlow onComplete={handleOnboardingComplete} />
            ) : (
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/houses/analyze" element={<HouseAnalyzer />} />
                <Route path="/houses/:id" element={<HouseDetails />} />
                <Route path="/profile" element={<div>Profile editing coming soon</div>} />
                <Route path="/onboarding" element={<OnboardingFlow onComplete={handleOnboardingComplete} />} />
              </Routes>
            )}
          </ProtectedRoute>
        } />
      </Routes>
    </Router>
  );
}

export default function App() {
  const { isAuthenticated, init } = useAuthStore();

  useEffect(() => {
    init();
  }, [init]);

  return (
    <>
      {!isAuthenticated ? <AuthFlow /> : <MainApp />}
    </>
  );
}
